// Shared portal UI building blocks
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export const PortalCard = ({ title, right, children, sx = {} }) => (
  <Box
    sx={{
      bgcolor: '#fff',
      border: '1px solid #DDE3E9',
      borderRadius: '6px',
      overflow: 'hidden',
      height: '100%',
      ...sx,
    }}
  >
    {title && (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #DDE3E9' }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: '#10151C',
          }}
        >
          {title}
        </Typography>
        {right}
      </Stack>
    )}
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

export const StatBlock = ({ label, value, sub, accent = '#0F766E' }) => (
  <Box
    sx={{
      bgcolor: '#fff',
      border: '1px solid #DDE3E9',
      borderRadius: '6px',
      p: 2.5,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: 4,
        height: '100%',
        bgcolor: accent,
      },
    }}
  >
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: '#4A5763',
        mb: 1,
      }}
    >
      {label}
    </Typography>
    <Typography
      className="serif"
      sx={{
        fontSize: { xs: 24, sm: 28, md: 32 },
        fontWeight: 700,
        color: '#10151C',
        lineHeight: 1,
        mb: sub ? 0.5 : 0,
        wordBreak: 'break-word',
      }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography sx={{ fontSize: 12, color: '#4A5763', fontWeight: 300 }}>{sub}</Typography>
    )}
  </Box>
);

export const StatsRow = ({ items, accent }) => (
  <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ mb: 3 }}>
    {items.map((it) => (
      <Grid key={it.label} item xs={6} md={3}>
        <StatBlock {...it} accent={it.accent || accent} />
      </Grid>
    ))}
  </Grid>
);

export const DataTable = ({ columns, rows }) => (
  <Box sx={{ overflowX: 'auto' }}>
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <Box component="thead" sx={{ bgcolor: '#F4F6F8' }}>
        <Box component="tr">
          {columns.map((c) => (
            <Box
              key={c}
              component="th"
              sx={{
                textAlign: 'left',
                px: 2,
                py: 1.5,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#4A5763',
                borderBottom: '1px solid #DDE3E9',
              }}
            >
              {c}
            </Box>
          ))}
        </Box>
      </Box>
      <Box component="tbody">
        {rows.map((row, i) => (
          <Box
            key={i}
            component="tr"
            sx={{
              '&:hover': { bgcolor: '#F4F6F8' },
              borderBottom: '1px solid #EDF1F4',
            }}
          >
            {row.map((cell, j) => (
              <Box
                key={j}
                component="td"
                sx={{ px: 2, py: 1.75, color: j === 0 ? '#10151C' : '#4A5763', fontWeight: j === 0 ? 600 : 400 }}
              >
                {cell}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);
